import React, { useState, useEffect } from 'react';
import { Snackbar, Button, Input, Grid } from '@mui/material';
import Paper from '@mui/material/Paper';
import MuiAlert from '@mui/material/Alert';
import { supabase } from "../../../supabaseClient";
import { decode } from 'base64-arraybuffer';

import GoBackButton from '../GoBack'

import SessionForm from './SessionForm';
import GalleryForm from './GalleryForm';
import GeneralInfoForm from './GeneralInfoForm';

import { Tabs, Tab } from '@mui/material';

const CreateOrUpdateCompany = ({ companyData, sessions, setSessions }) => {
  const [isDemo, setIsDemo] = useState(false);
  const [isSimpleMode, setIsSimpleMode] = useState(false);
  const [name, setName] = useState('');
  const [color, setColor] = useState('');

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const [images, setImages] = useState([]);
  const [logoFile, setLogoFile] = useState('');
  const [iconFile, setIconFile] = useState('');

  const [tabValue, setTabValue] = useState(0);
  
  useEffect(() => {
    if (companyData) {
      setIsDemo(companyData.demo);
      setIsSimpleMode(companyData.simple_mode);
      setName(companyData.name);
      setColor(companyData.color);
      setSessions(companyData.sessions || []);
    }
  }, [companyData]);

  const submitSessions = async (companyId) => {
    for (const session of sessions) {
      // Prepare the session data to be inserted
      const sessionData = {
        id: session.id,
        name: session.name,
        subtitle: session.subtitle,
        location: session.location,
        lat: session.latitude,
        long: session.longitude,
        date_from: session.date_from,
        //date_to: session.date_to,
        icon: session.icon,
        company: companyId,
      };
  
      // Insert the session into the 'sessions' table
      const { data: sessionInsertData, error: sessionInsertError } = await supabase
        .from('sessions')
        .insert(sessionData);
        
      if (sessionInsertError) {
        console.error('Error inserting session:', sessionInsertError);
        // Puedes manejar el error según tus necesidades, por ejemplo, puedes hacer un rollback de las inserciones realizadas anteriormente.
      }
    }
  };
  
  const updateSessions = async (companyId) => {
    // Retrieve sessions from Supabase for the given company
    const { data: existingSessions, error: fetchError } = await supabase
      .from('sessions')
      .select('*')
      .eq('company', companyId);
  
    if (fetchError) {
      console.error('Error fetching existing sessions:', fetchError);
      // Handle the fetch error accordingly
      return;
    }
  
    // Prepare a set of session IDs from the list of sessions
    const sessionIds = new Set(sessions.map(session => session.id));
  
    // Iterate through existing sessions and delete those not present in the list
    for (const existingSession of existingSessions) {
      if (!sessionIds.has(existingSession.id)) {
        const { data: deleteData, error: deleteError } = await supabase
          .from('sessions')
          .delete()
          .match({ id: existingSession.id });
  
        if (deleteError) {
          console.error('Error deleting session:', deleteError);
          // Handle the delete error accordingly
        }
      }
    }
  
    // Iterate through the list of sessions and update or insert each session
    for (const session of sessions) {
      // Prepare the session data to be inserted or updated
      const sessionData = {
        name: session.name,
        subtitle: session.subtitle,
        location: session.location,
        lat: session.latitude,
        long: session.longitude,
        date_from: session.date_from,
        //date_to: session.date_to,
        icon: session.icon,
        company: companyId,
      };
  
      if (session.id) {
        // Session has an id, perform update
        const { data: sessionUpdateData, error: sessionUpdateError } = await supabase
          .from('sessions')
          .update(sessionData)
          .match({ id: session.id });
  
        if (sessionUpdateError) {
          console.error('Error updating session:', sessionUpdateError);
          // Handle the update error accordingly
        }
      } else {
        // Session doesn't have an id, perform insert
        const { data: sessionInsertData, error: sessionInsertError } = await supabase
          .from('sessions')
          .insert(sessionData);
  
        if (sessionInsertError) {
          console.error('Error inserting session:', sessionInsertError);
          // Handle the insert error accordingly
        }
      }
    }
  };
  

  const convertBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.readAsDataURL(file)
      fileReader.onload = () => {
        resolve(fileReader.result);
      }
      fileReader.onerror = (error) => {
        reject(error);
      }
    })
  }

  const uploadImagesToBucket = async (images, companyId) => {
    try {
      for (let i = 0; i < images.length; i++) {
        const image = images[i];
        const fileData = image.data_url.split(",")[1]; // Obtén el base64 de la imagen
        const timestamp = Date.now();

        // Sube la imagen al bucket en la carpeta correspondiente al ID dado
        const { data, error: uploadError } = await supabase.storage
          .from("photos")
          .upload(`${companyId}/${timestamp}.png`, decode(fileData), {
            contentType: "image/png",
            upsert: true
          });

        if (uploadError) {
          console.error("Error al subir la imagen:", uploadError);
        } else {
          console.log("Imagen subida exitosamente:", data);
        }
      }
    } catch (error) {
      console.error("Error al subir las imágenes:", error);
    }
  };

  const uploadImageToBucket = async (image, folder, companyId) => {
    try {
      const fileData = await convertBase64(image)
      console.log(fileData.split('base64,')[1])

      // Sube la imagen al bucket en la carpeta correspondiente al ID dado
      const { data, error: uploadError } = await supabase.storage
        .from(folder)
        .upload(`${companyId}.png`, decode(fileData.split('base64,')[1]), {
          contentType: "image/png",
          upsert: true
        });
  
      if (uploadError) {
        console.error("Error al subir la imagen:", uploadError);
      } else {
        console.log("Imagen subida exitosamente:", data);
      }
    } catch (error) {
      console.error("Error al subir la imagen:", error);
    }
  };

  const handleSubmit = async (event) => {
    if(event) event.preventDefault();

    if (companyData) {
      // Realizar lógica para actualizar la compañía
      const { error: updateError } = await supabase
        .from('companies')
        .update({
          demo: isDemo,
          simple_mode: isSimpleMode,
          name,
          color,
        })
        .eq('id', companyData.id);

      if (updateError) {
        console.error(updateError);
        // Mostrar una notificación de error
        setSnackbarMessage('Error al actualizar la empresa');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return;
      }

      console.log('Company record updated successfully');
      // Mostrar una notificación de éxito
      setSnackbarMessage('Empresa actualizada correctamente');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);

      updateSessions(companyData.id);

      uploadImagesToBucket(images, companyData.id);
      uploadImageToBucket(logoFile, 'logo', companyData.id)
      uploadImageToBucket(iconFile, 'icons', companyData.id)
    } else {
      // Realizar lógica para crear una nueva compañía
      const { data: insertedCompany, error } = await supabase.from('companies').insert([
        {
          demo: isDemo,
          simple_mode: isSimpleMode,
          name,
          color,
        }
      ])
        .select();

      if (error) {
        console.error(error);
        // Mostrar una notificación de error
        setSnackbarMessage('Error al crear la empresa');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return;
      }

      console.log('Company record created successfully:', insertedCompany);
      // Mostrar una notificación de éxito
      setSnackbarMessage('Empresa creada correctamente');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);

      submitSessions(insertedCompany[0].id);

      uploadImagesToBucket(images, insertedCompany[0].id);
      uploadImageToBucket(logoFile, 'logo',insertedCompany[0].id)
      uploadImageToBucket(iconFile, 'icons',insertedCompany[0].id)
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleSessionAdd = (session) => {
    setSessions([...sessions, session]);
  };

  const handleSessionDelete = (session) => {
    const shouldDelete = window.confirm('Estas seguro que deseas borrar esta sesión?');
    if (shouldDelete) {
      const updatedSessions = sessions.filter((s) => s !== session);
      setSessions(updatedSessions);
    }
  };

  const handleSessionUpdate = (session) => {
    const updatedSessions = sessions.map((s) => {
      if (s.id === session.id) {
        return { ...s, ...session };
      }
      return s;
    });
    setSessions(updatedSessions);
  };
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };


  return (
    <Paper
      sx={{
        p: 2,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <MuiAlert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>

      <Grid container spacing={2}>
        <Grid item md={2}>
          <GoBackButton />
        </Grid>
        <Grid item md={12}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="Informacion General" />
            <Tab label="Sesiones" />
            <Tab label="Galeria" />
          </Tabs>

          {tabValue === 0 && <GeneralInfoForm
            id={companyData?.id}
            setName={setName}
            name={name}
            color={color}
            setColor={setColor}
            isDemo={isDemo}
            setIsDemo={setIsDemo}
            isSimpleMode={isSimpleMode}
            setIsSimpleMode={setIsSimpleMode}
            logoFile={logoFile}
            setLogoFile={setLogoFile}
            iconFile={iconFile}
            setIconFile={setIconFile}
          />}
          {tabValue === 1 && <SessionForm 
            onSessionAdd={handleSessionAdd} 
            onSessionUpdate={handleSessionUpdate} 
            sessions={sessions}
            onSessionDelete={handleSessionDelete }
             />}
          {tabValue === 2 && <GalleryForm  images={images} setImages={setImages} companyId={companyData?.id} />}
        </Grid>
      </Grid>

      <Grid item md={12}>
        <Button onClick={handleSubmit} type="submit" variant="contained" color="success" fullWidth>
          {companyData ? 'Actualizar Empresa' : 'Crear Empresa'}
        </Button>
      </Grid>
    </Paper>
  );
};

export default CreateOrUpdateCompany;